// ============================================================
// tema.js — Identidade visual e constantes de domínio
//
// Cores, meses, núcleos e tipos de dispensa usados em todo o painel.
// Extraído do App.js (Fase 1, Parte B) sem alterar nenhum valor.
// ============================================================

// ── Constantes ───────────────────────────────────────────────
// Paleta derivada da logomarca Revelar Talentos
// (azul #3055A5, verde #4DB033, amarelo #FCDA18)
export const AZUL = "#3055A5";          // cor institucional primária (da logo)
export const AZUL_ESCURO = "#24417D";
export const AZUL_CLARO = "#EAF0FA";
export const VERDE = "#3055A5";         // "VERDE" agora aponta para o azul institucional
export const VERDE_ESCURO = "#24417D";  // (nomes mantidos p/ não quebrar referências)
export const VERDE_CLARO = "#EAF0FA";
export const VERDE_LOGO = "#4DB033";    // verde vivo da logo, p/ acentos pontuais
export const AMARELO = "#FCDA18";       // amarelo da logo, p/ acentos
export const CINZA = "#F4F6F9";
export const CINZA_ESC = "#64748B";
export const TEXTO = "#1A2233";
export const BRANCO = "#FFFFFF";
export const ALERTA_CL = "#FEF3C7";
export const ALERTA = "#D97706";
export const PERIGO = "#DC2626";
export const PERIGO_CL = "#FEE2E2";
export const INFO = "#3055A5";
export const INFO_CL = "#EAF0FA";

// A cor de AÇÃO: o botão que faz a coisa acontecer e o item de menu em que
// se está agora. É o azul da marca alguns graus mais vivo, e é o único
// ponto saturado de cada tela — por isso funciona. Se o painel tivesse
// três botões azuis brilhantes lado a lado, nenhum seria o principal.
export const ACAO = "#1D63E8";
export const ACAO_ESCURO = "#1A55C8";

export const MESES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];
export const TIPOS_DISPENSA = {
  COLETIVA: {
    label: "Coletiva",
    desc: "Afeta todos os membros do núcleo",
    cor: "#7C3AED",
    corClaro: "#EDE9FE",
    subtipos: [
      "Feriado nacional",
      "Feriado estadual",
      "Feriado municipal",
      "Data imprensada",
      "Suspensão técnica (chuva/clima)",
      "Outro coletivo",
    ],
  },
  MODALIDADE: {
    label: "Por Modalidade",
    desc: "Afeta todos os atletas de uma modalidade",
    cor: "#D97706",
    corClaro: "#FEF3C7",
    subtipos: [
      "Suspensão técnica (chuva/clima)",
      "Competição/evento da modalidade",
      "Day off - Pré/Pós competição",
      "Manutenção de equipamento/pista",
      "Ausência do técnico",
      "Outro por modalidade",
    ],
  },
  INDIVIDUAL: {
    label: "Individual",
    desc: "Afeta apenas um atleta/membro",
    cor: "#0891B2",
    corClaro: "#CFFAFE",
    subtipos: [
      "Atestado médico",
      "Declaração escolar",
      "Viagem",
      "Justificado ao coordenador",
      "Em competição",
      "Dispensa técnica",
      "Outro individual",
    ],
  },
};

// ── Utilitário de cor ────────────────────────────────────────
// calculo.js usa cores no formato ARGB do Excel (ex: "FF00853B" — os 2
// primeiros dígitos são o canal alfa). O painel usa CSS puro, então essa
// função descarta o canal alfa. Usada pelo Painel do Técnico e pelos
// gráficos do Dashboard (Fase 4) — fonte única, não duplicar em cada arquivo.
export function argbParaCss(argb) {
  return "#" + (argb || "FF64748B").slice(2);
}

// ── Base visual ──────────────────────────────────────────────
// Escalas fixas, para o painel inteiro ter o mesmo ritmo. Antes cada tela
// escolhia o seu espaçamento, o seu raio e o seu tamanho de fonte, e o
// resultado é o que se vê: tudo correto e nada com peso definido.
//
// São poucos degraus de propósito. Escala com degrau demais volta a ser
// escolha livre, e a diferença entre 13 e 13,5 não significa nada para
// quem lê.

// Espaçamento, em píxeis. Múltiplos de 4, que é o que o olho acompanha.
export const ESP = { xs: 4, sm: 8, md: 12, lg: 16, xl: 24, xxl: 32 };

// Raio de canto. Campo e botão no mesmo degrau; cartão um acima.
export const RAIO = { sm: 7, md: 10, lg: 14, pilula: 999 };

// Sombra. Sutil por padrão: o painel é de trabalho, não de vitrine.
export const SOMBRA = {
  nenhuma: "none",
  cartao: "0 1px 2px rgba(16, 24, 40, 0.04)",
  suspenso: "0 6px 20px rgba(16, 24, 40, 0.10)",
};

// Tipografia. Um nome por papel, não por tamanho: quem escreve tela pensa
// em "é um rótulo" e não em "é 11,5 pixels".
export const TIPO = {
  titulo:   { fontSize: 19,   fontWeight: 800, letterSpacing: -0.2 },
  secao:    { fontSize: 15.5, fontWeight: 700 },
  corpo:    { fontSize: 13.5, fontWeight: 400, lineHeight: 1.6 },
  apoio:    { fontSize: 12.5, fontWeight: 400, lineHeight: 1.55 },
  rotulo:   { fontSize: 11,   fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" },
  numero:   { fontSize: 26,   fontWeight: 800, fontVariantNumeric: "tabular-nums" },
};

// Bordas e fundos neutros, para não haver seis cinzas diferentes no painel.
export const BORDA = "#E5E7EB";
export const BORDA_FORTE = "#D1D5DB";
export const FUNDO_SUAVE = "#F8FAFC";

// Cores de significado. Separadas da paleta da marca: o azul institucional
// não quer dizer "está bom", e verde não é enfeite.
export const OK = "#047857";
export const OK_CL = "#DCFCE7";
export const ATENCAO = "#B45309";
export const ATENCAO_CL = "#FEF3C7";
export const RUIM = "#B91C1C";
export const RUIM_CL = "#FEE2E2";
export const NEUTRO_CL = "#F1F5F9";

// ── Barra lateral escura ─────────────────────────────────────
// O menu passou a ser um bloco escuro, e não mais uma coluna branca
// encostada noutra coluna branca. O motivo é de leitura, não de gosto: com
// tudo claro, a navegação e o conteúdo disputavam o mesmo plano, e o olho
// precisava procurar a borda para saber onde uma coisa terminava. Escuro,
// o menu vira moldura e o conteúdo vira o assunto.
//
// O azul é o da logomarca escurecido, não um azul novo: a marca continua
// sendo a mesma em toda a tela.
export const MENU = {
  fundo: "#16346F",
  fundoTopo: "#122B5C",     // faixa da logo, um degrau mais fundo
  item: "#BBCBE6",          // item em repouso: legível, sem competir
  itemHover: "#1D3F80",
  itemAtivo: "#1D63E8",     // o azul vivo do ativo, único ponto saturado
  grupo: "#7E9BCB",         // o rótulo do módulo, discreto de propósito
  divisor: "#24457F",
};

// ── Cores dos avatares ───────────────────────────────────────
// A inicial de cada pessoa num círculo colorido. A cor não significa nada
// (não é perfil, não é situação): serve só para o olho reencontrar a mesma
// linha ao rolar a lista. Por isso é sorteada pelo nome e não por dado —
// assim a mesma pessoa tem sempre a mesma cor, em qualquer tela.
export const AVATARES = [
  { bg: "#DBEAFE", cor: "#1D4ED8" },
  { bg: "#EDE9FE", cor: "#6D28D9" },
  { bg: "#D1FAE5", cor: "#047857" },
  { bg: "#FFEDD5", cor: "#C2410C" },
  { bg: "#FCE7F3", cor: "#BE185D" },
  { bg: "#CFFAFE", cor: "#0E7490" },
];

// Sempre a mesma cor para o mesmo nome. Soma simples dos caracteres: não
// precisa ser bem distribuída, precisa ser estável.
export function corDoAvatar(nome) {
  const texto = String(nome || "?");
  let soma = 0;
  for (let i = 0; i < texto.length; i++) soma += texto.charCodeAt(i);
  return AVATARES[soma % AVATARES.length];
}

// Iniciais para o avatar: primeira letra do primeiro nome e do último.
export function iniciaisDoNome(nome) {
  const partes = String(nome || "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0][0].toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

// ── Tipografia ───────────────────────────────────────────────
// Uma família só, declarada num lugar só. Antes cada tela repetia a pilha
// do sistema à mão, e bastava esquecer uma para aquele pedaço da página
// renderizar com outra fonte.
export const FONTE = "'Inter', -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif";

// O título da página, no alto do conteúdo. Grande de propósito: é o que
// diz onde a pessoa está, e competia em tamanho com os títulos de cartão.
export const TIPO_PAGINA = {
  titulo: { fontSize: 30, fontWeight: 800, letterSpacing: -0.7, lineHeight: 1.15 },
  sub: { fontSize: 14.5, fontWeight: 400, lineHeight: 1.5 },
  migalha: { fontSize: 12.5, fontWeight: 600 },
};
